import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useDashboardV3 } from '../contexts/DashboardV3Context';
import { V3PrefsProvider, useV3Prefs } from './V3PrefsContext';
import { V3AlertsProvider } from './V3AlertsContext';
import { V3ShellContext } from './V3ShellContext';
import { isV3Bypassed, isV3BareRoute, isV3NewUiRoute } from './v3Config';
import { getPageZoom } from '../utils/desktopZoomFix';
import V3LeftBar from './V3LeftBar';
import V3RightBar from './V3RightBar';
import V3TopBar from './V3TopBar';
import ActivityModalHost from '../components/ActivityModalHost';
import V3UserPrefs from './V3UserPrefs';
import V3LegacyWindows from './V3LegacyWindows';
import './v3.css';

// The frame every signed-in Main UI page is drawn inside while UI V3 is switched on for the person looking:
// the patterned backdrop, the top bar, the two edge bars, and the page itself on a white surface until it is rebuilt for V3.
const V3Frame = ({ user, onLogout, children }) => {
  const { theme, widths, overlay, closeOverlay, setCompactBars, modalOpen, setModalNeed } = useV3Prefs();
  const location = useLocation();
  const [toolbarSlot, setToolbarSlot] = useState(null);
  // Set by a page that has its own side panels (Transport): the top bar and control bar then sit over the page's middle only.
  const [panelsOn, setPanelsOn] = useState(false);
  // Where the page control bar sits: 'top' (every page) or 'bottom' (Transport asks for this).
  const [toolbarAt, setToolbarAt] = useState('top');
  // The empty space in the top bar, between the search and the theme button, that a page can fill (see V3TopBar).
  const [topBarSlot, setTopBarSlot] = useState(null);
  const slotRef = useCallback((node) => { setToolbarSlot(node); }, []);

  // Every scroll bar follows the V3 look (see v3.css). The theme is put on <html> so it also reaches menus and windows drawn on the page body.
  useEffect(() => {
    document.documentElement.setAttribute('data-v3', theme);
    return () => document.documentElement.removeAttribute('data-v3');
  }, [theme]);

  // Windows (modals) tell the shell they are open and how much width they want; the prefs context then pushes the bars shut if the room
  // between them is short (see `modes` in V3PrefsContext).
  const [modalWants, setModalWants] = useState({});
  const registerModal = useCallback((id, want) => {
    setModalWants((previous) => ({ ...previous, [id]: want }));
    return () => setModalWants((previous) => { const next = { ...previous }; delete next[id]; return next; });
  }, []);
  const modalNeed = Object.values(modalWants).reduce((most, want) => Math.max(most, want), 0);
  useEffect(() => {
    setModalNeed(modalNeed);
    return () => setModalNeed(0);
  }, [modalNeed, setModalNeed]);

  // Where windows start: just under the top bar and the control bar (in the page's own pixels, so it is right at any zoom).
  const [regionTop, setRegionTop] = useState(72);
  useEffect(() => {
    if (!modalOpen) return undefined;
    const measure = () => {
      const zoom = getPageZoom() || 1;
      // Windows start just under the main header, so they sit over the page's own control bar.
      const header = document.querySelector('.v3-top');
      setRegionTop(Math.max(0, Math.round(header ? header.getBoundingClientRect().bottom / zoom + 8 : 72)));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [modalOpen, toolbarSlot, toolbarAt]);

  // While a window is open, an open side bar closes the moment the person clicks anywhere outside the bars (the window included). The click
  // still goes through to what was clicked, so nothing is lost; clicking a bar, its edge handle or the buttons that open it never counts.
  useEffect(() => {
    if (!modalOpen || (!overlay.left && !overlay.right)) return undefined;
    const onPointerDown = (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest('.v3-bar, .v3-handle')) return;
      closeOverlay();
    };
    document.addEventListener('pointerdown', onPointerDown, true);
    return () => document.removeEventListener('pointerdown', onPointerDown, true);
  }, [modalOpen, overlay.left, overlay.right, closeOverlay]);

  // An overlay bar closes when the person goes to another page, and with Escape.
  useEffect(() => { closeOverlay(); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!overlay.left && !overlay.right) return undefined;
    const onKey = (event) => { if (event.key === 'Escape') closeOverlay(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [overlay.left, overlay.right, closeOverlay]);

  // A page tells the shell about its layout:  { panels: true } when it has its own full-height side panels beside its main area,
  // { compact: true } when it needs both edge bars kept as icon strips that open over the page. Pass nothing to reset.
  const setPageLayout = useCallback((layout) => {
    setPanelsOn(!!(layout && layout.panels));
    setCompactBars(!!(layout && layout.compact));
    setToolbarAt(layout && layout.toolbar === 'bottom' ? 'bottom' : 'top');
  }, [setCompactBars]);

  // What a window needs to sit in the content area: the bars' widths (the window is drawn on the page body, outside this frame) and where it starts.
  const regionVars = useMemo(() => ({ '--v3-left-w': `${widths.left}px`, '--v3-right-w': `${widths.right}px` }), [widths.left, widths.right]);
  const shellValue = useMemo(
    () => ({ active: true, toolbarSlot, topBarSlot, theme, setPageLayout, registerModal, regionTop, regionVars }),
    [toolbarSlot, topBarSlot, theme, setPageLayout, registerModal, regionTop, regionVars],
  );
  const bare = isV3BareRoute(location.pathname);

  return (
    <V3ShellContext.Provider value={shellValue}>
      <V3AlertsProvider user={user}>
      <div
        className="v3-root"
        data-v3-theme={theme}
        data-v3-panels={panelsOn ? 'on' : 'off'}
        data-v3-toolbar={toolbarAt}
        data-v3-content={isV3NewUiRoute(location.pathname) ? 'home' : 'page'}
        data-testid="v3-shell"
        style={{ '--v3-left-w': `${widths.left}px`, '--v3-right-w': `${widths.right}px` }}
      >
        <div className="v3-backdrop" aria-hidden="true" />
        <V3LeftBar user={user} />
        <V3TopBar user={user} onLogout={onLogout} slotRef={setTopBarSlot} />
        <div className="v3-main">
          <div className="v3-toolbar-slot" ref={slotRef} data-testid="v3-toolbar-slot" />
          <div className={`v3-legacy ${bare ? 'v3-legacy--bare' : ''}`} data-testid="v3-page-surface">{children}</div>
        </div>
        <V3RightBar />
        {(overlay.left || overlay.right) && (
          <div className="v3-scrim" onClick={closeOverlay} aria-hidden="true" data-testid="v3-scrim" style={modalOpen ? { display: 'none' } : undefined} />
        )}
        {/* The over-the-page left bar stays mounted (hidden) while a window is open, so a window that was opened from it is not closed when the bar closes. */}
        {(overlay.left || modalOpen) && <V3LeftBar user={user} overlay={true} parked={!overlay.left} />}
        {overlay.right && <V3RightBar overlay={true} />}
      </div>
        {/* Windows reopened from Recents / Open are built here, inside the shell, so they get the same V3 window look as when they are opened from the navigation. */}
        <ActivityModalHost />
        {/* My preferences: opened from the user circle on the left bar. Built here, inside the shell, so it is a V3 window like Customer Management. */}
        <V3UserPrefs user={user} onLogout={onLogout} />
        {/* Every old-style pop-up that opens (a fixed layer that centres one panel) is turned into a V3 window from the outside, without editing the file that draws it. */}
        <V3LegacyWindows />
      </V3AlertsProvider>
    </V3ShellContext.Provider>
  );
};

/**
 * Wraps the Main UI routes in App.js. UI V3 is shown ONLY when Settings > Dashboard V3 has it switched on for this person on the
 * Main UI (useDashboardV3 → v3On with activeUi 'main'). Otherwise, and on the routes in V3_BYPASS_ROUTES, it renders its
 * children untouched, so everyone else sees exactly the dashboard they see today.
 */
export const V3MainGate = ({ user, onLogout, children }) => {
  const { v3On, activeUi } = useDashboardV3();
  const location = useLocation();
  if (!v3On || activeUi !== 'main' || !user || isV3Bypassed(location.pathname)) return <>{children}<ActivityModalHost /></>;
  return (
    <V3PrefsProvider user={user}>
      <V3Frame user={user} onLogout={onLogout}>{children}</V3Frame>
    </V3PrefsProvider>
  );
};

export default V3MainGate;
